import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../../../constants';
import { fetchTableSettings } from './api';

const defaultViewedState = {
  newViewed: [],
  notViewedUuids: [],
  wasSentViewedUuids: [],
};

export const tableSlice = createSlice({
  name: 'table',
  initialState: {
    settings: null,
    status: REQUEST_STATUS.idle,
    error: null,
    canShowShimmer: false,
    viewState: defaultViewedState,
  },
  reducers: {
    updateColumnOrderByTableName: (state, action) => {
      state.settings[action.payload.tableKey].columnOrder = [...action.payload.newOrder];
    },
    updateColumnVisibilityByTableName: (state, action) => {
      state.settings[action.payload.tableKey].columnVisibility = action.payload.newVisibility;
    },
    updateColumnSizingByTableName: (state, action) => {
      state.settings[action.payload.tableKey].columnSizes = action.payload.newColumnSizing;
    },
    updateColumnPinningByTableName: (state, action) => {
      state.settings[action.payload.tableKey].columnPinning = action.payload.newColumnPinning;
    },
    updateShowColumnFilters: (state, action) => {
      state.settings.commonTable.showColumnFilters = action.payload;
    },
    updateDensity: (state, action) => {
      state.settings.commonTable.density = action.payload;
    },
    updateIsFullScreen: (state, action) => {
      state.settings.isFullScreen = action.payload.newIsFullScreen;
    },
    updateCanShowShimmer: (state, action) => {
      state.canShowShimmer = action.payload;
    },
    addViewedState: (state, action) => {
      state.viewState = {
        ...state.viewState,
        ...action.payload,
      };
    },
    addViewedItemState: (state, action) => {
      state.viewState = {
        wasSentViewedUuids: [...new Set([...state.viewState.wasSentViewedUuids, action.payload])],
        newViewed: [...new Set([...state.viewState.newViewed, action.payload])],
      };
    },
    resetViewedRows: (state) => {
      state.viewState = defaultViewedState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchTableSettings.pending, (state) => {
        state.status = REQUEST_STATUS.loading;
      })
      .addCase(fetchTableSettings.rejected, (state) => {
        state.status = REQUEST_STATUS.failed;
      })
      .addCase(fetchTableSettings.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.status = REQUEST_STATUS.succeeded;
          state.settings = action.payload.data.data.preferences;
        } else {
          state.status = REQUEST_STATUS.failed;
          state.error = action?.payload?.error?.message;
        }
      });
  },
});

export const {
  updateColumnOrderByTableName,
  updateColumnVisibilityByTableName,
  updateColumnSizingByTableName,
  updateColumnPinningByTableName,
  updateShowColumnFilters,
  updateDensity,
  updateIsFullScreen,
  updateCanShowShimmer,
  addViewedState,
  resetViewedRows,
  addViewedItemState,
} = tableSlice.actions;

export default tableSlice.reducer;

export const TABLE_KEYS = {
  assessmentsTable: 'surveysTable',
  assessmentsDSTable: 'surveysDSTable',
  programsTable: 'programsTable',
  programsDSTable: 'programsDSTable',
  patientsTable: 'patientsTable',
  patientsDSTable: 'patientsDSTable',
  resourcesTable: 'resourcesTable',
  resourcesDSTable: 'resourcesDSTable',
  claimsTable: 'claimsTable',
  claimsDSTable: 'claimsDSTable',
  ordersTable: 'ordersTable',
  ordersDSTable: 'ordersDSTable',
  accountsTable: 'accountsTable',
  alertsTable: 'alertsTable',
  alertsDSTable: 'alertsDSTable',
  usersTable: 'usersTable',
  usersDSTable: 'usersDSTable',
  claimReportsTable: 'claimReportsTable',
  claimReportsDSTable: 'claimReportsDSTable',
  reportableTable: 'reportablesTable',
  reportableDSTable: 'reportablesDSTable',
  allowedAccountsTable: 'allowedAccountsTable',
  dashboardAssessmentsAlertsTable: 'dashboardAssessmentsAlertsTable',
  cptsTable: 'cptsTable',
  botActivitiesTable: 'botActivitiesTable',
  botActivitiesDSTable: 'botActivitiesDSTable',
};

export const TABLE_NAMES = {
  [TABLE_KEYS.programsTable]: 'programs',
  [TABLE_KEYS.programsDSTable]: 'programs',
  [TABLE_KEYS.assessmentsTable]: 'surveys',
  [TABLE_KEYS.assessmentsDSTable]: 'surveys',
  [TABLE_KEYS.patientsTable]: 'patients',
  [TABLE_KEYS.patientsDSTable]: 'patients',
  [TABLE_KEYS.resourcesTable]: 'resources',
  [TABLE_KEYS.resourcesDSTable]: 'resources',
  [TABLE_KEYS.ordersTable]: 'orders',
  [TABLE_KEYS.ordersDSTable]: 'orders',
  [TABLE_KEYS.claimsTable]: 'claims',
  [TABLE_KEYS.claimsDSTable]: 'claims',
  [TABLE_KEYS.claimReportsTable]: 'claimReports',
  [TABLE_KEYS.claimReportsDSTable]: 'claimReports',
  [TABLE_KEYS.reportableTable]: 'reportables',
  [TABLE_KEYS.reportableDSTable]: 'reportables',
  [TABLE_KEYS.accountsTable]: 'accounts',
  [TABLE_KEYS.allowedAccountsTable]: 'accounts',
  [TABLE_KEYS.alertsTable]: 'alerts',
  [TABLE_KEYS.alertsDSTable]: 'alerts',
  [TABLE_KEYS.usersTable]: 'users',
  [TABLE_KEYS.dashboardAssessmentsAlertsTable]: 'dashboardAssessmentsAlerts',
  [TABLE_KEYS.cptsTable]: 'cpts',
  [TABLE_KEYS.botActivitiesTable]: 'botActivities',
  [TABLE_KEYS.botActivitiesDSTable]: 'botActivities',
};

export const DEFAULT_TABLES_SORTING = {
  [TABLE_KEYS.assessmentsTable]: { id: 'orderDate', desc: true },
  [TABLE_KEYS.assessmentsDSTable]: { id: 'orderDate', desc: true },
  [TABLE_KEYS.programsTable]: { id: 'createdTimestamp', desc: true },
  [TABLE_KEYS.programsDSTable]: { id: 'createdTimestamp', desc: true },
  [TABLE_KEYS.patientsTable]: { id: 'lastName', desc: false },
  [TABLE_KEYS.patientsDSTable]: { id: 'lastName', desc: false },
  [TABLE_KEYS.resourcesTable]: { id: 'orderTimestamp', desc: true },
  [TABLE_KEYS.resourcesDSTable]: { id: 'orderTimestamp', desc: true },
  [TABLE_KEYS.claimsTable]: { id: 'claimGeneratedTimestamp', desc: true },
  [TABLE_KEYS.claimsDSTable]: { id: 'claimGeneratedTimestamp', desc: true },
  [TABLE_KEYS.ordersTable]: { id: 'orderDate', desc: true },
  [TABLE_KEYS.ordersDSTable]: { id: 'orderDate', desc: true },
  [TABLE_KEYS.accountsTable]: { id: 'businessName', desc: false },
  [TABLE_KEYS.alertsTable]: { id: 'createdTimestamp', desc: true },
  [TABLE_KEYS.alertsDSTable]: { id: 'createdTimestamp', desc: true },
  [TABLE_KEYS.usersTable]: { id: 'firstName', desc: false },
  [TABLE_KEYS.usersDSTable]: { id: 'firstName', desc: false },
  [TABLE_KEYS.claimReportsTable]: { id: 'claimReportGeneratedTimestamp', desc: true },
  [TABLE_KEYS.claimReportsDSTable]: { id: 'claimReportGeneratedTimestamp', desc: true },
  [TABLE_KEYS.reportableTable]: { id: 'reportableGeneratedTimestamp', desc: true },
  [TABLE_KEYS.reportableDSTable]: { id: 'reportableGeneratedTimestamp', desc: true },
  [TABLE_KEYS.botActivitiesTable]: { id: 'documentUploadTimestamp', desc: true },
  [TABLE_KEYS.botActivitiesDSTable]: { id: 'documentUploadTimestamp', desc: true },
  [TABLE_KEYS.allowedAccountsTable]: { id: 'businessName', desc: false },
}
